import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { Input } from './ui/input';

export const RESOURCES_SEARCH_DEBOUNCE_MS = 300;

interface ResourcesSearchInputProps {
  isFetching: boolean;
  onDebouncedChange: (value: string) => void;
}

export const ResourcesSearchInput: React.FC<ResourcesSearchInputProps> = React.memo(
  ({ isFetching, onDebouncedChange }) => {
    const { t } = useLocale();
    const [value, setValue] = useState('');
    const [debouncedValue, setDebouncedValue] = useState('');
    const onDebouncedChangeRef = useRef(onDebouncedChange);
    const hasEmittedInitialRef = useRef(false);

    onDebouncedChangeRef.current = onDebouncedChange;

    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(value);
      }, RESOURCES_SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
      if (!hasEmittedInitialRef.current) {
        hasEmittedInitialRef.current = true;
        return;
      }
      onDebouncedChangeRef.current(debouncedValue.trim());
    }, [debouncedValue]);

    const isDebouncing = value.trim() !== debouncedValue.trim();
    const searchActive = value.trim() !== '' || debouncedValue.trim() !== '';
    const showSearchSpinner = isDebouncing || (isFetching && searchActive);

    return (
      <div
        className="relative min-w-0 w-full min-[600px]:w-auto min-[600px]:flex-[2]"
        aria-busy={showSearchSpinner}
      >
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
        </span>
        <Input
          type="text"
          placeholder={t('Search resources...')}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          className={`min-w-[140px] w-full pl-9 pr-4 text-sm ${showSearchSpinner ? 'pr-11' : ''}`}
        />
        {showSearchSpinner && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          </span>
        )}
      </div>
    );
  }
);

ResourcesSearchInput.displayName = 'ResourcesSearchInput';
